import React from 'react';
import { Box, Grid, Typography, useTheme, Chip, Alert, Stack } from "@mui/material";
import { styled } from "@mui/material/styles";
import StorageIcon from '@mui/icons-material/Storage';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import TerminalIcon from '@mui/icons-material/Terminal';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import { useTarefa } from "../../../../contexts/TarefaContext";
import { TipoTarefa } from "../../../../types/tarefa";

const CardOpcao = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'selecionado' && prop !== 'destaque',
})<{ selecionado: boolean; destaque?: boolean }>(({ theme, selecionado, destaque }) => ({
    padding: theme.spacing(2.5),
    borderRadius: '12px',
    border: `2px solid ${selecionado ? theme.palette.primary.main : theme.palette.divider}`,
    backgroundColor: selecionado
        ? (theme.palette.mode === 'dark' ? 'rgba(42, 81, 247, 0.15)' : 'rgba(42, 81, 247, 0.04)')
        : theme.palette.background.paper,
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '135px',
    position: 'relative',
    '&:hover': {
        transform: 'translateY(-2px)',
        borderColor: theme.palette.primary.main,
        boxShadow: theme.shadows[2],
    },
}));

const CardPlataforma = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'selecionado',
})<{ selecionado: boolean }>(({ theme, selecionado }) => ({
    padding: theme.spacing(2),
    borderRadius: '12px',
    border: `2px solid ${selecionado ? theme.palette.primary.main : theme.palette.divider}`,
    backgroundColor: selecionado
        ? (theme.palette.mode === 'dark' ? 'rgba(42, 81, 247, 0.15)' : 'rgba(42, 81, 247, 0.05)')
        : theme.palette.background.paper,
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    '&:hover': {
        transform: 'translateY(-1px)',
        borderColor: theme.palette.primary.main,
    },
}));

const FormTipoTarefa: React.FC = () => {
    const [tarefa, setTarefa] = useTarefa();
    const theme = useTheme();

    const plataformaAtual = (tarefa.plataforma || 'windows').toLowerCase();

    const selecionarPlataforma = (novaPlataforma: 'windows' | 'linux') => {
        let novoTipo = tarefa.tipo;
        // Se mudou para linux e o tipo atual for exclusivo do windows, migra para Postgres
        if (novaPlataforma === 'linux' && (tarefa.tipo === 'SQLServer' || tarefa.tipo === 'Sybase' || tarefa.tipo === 'CheckDB' || !tarefa.tipo)) {
            novoTipo = 'Postgres';
        }
        setTarefa({ ...tarefa, plataforma: novaPlataforma, tipo: novoTipo });
    };

    const selecionarTipo = (tipo: TipoTarefa) => {
        setTarefa({ ...tarefa, tipo });
    };

    // Opções para Windows
    const opcoesWindows = [
        { valor: 'SQLServer' as TipoTarefa, label: 'SQL Server', descricao: 'Backup Microsoft SQL Server', icone: <StorageIcon sx={{ fontSize: 38 }} /> },
        { valor: 'Postgres' as TipoTarefa, label: 'Postgres', descricao: 'Backup PostgreSQL', icone: <StorageIcon sx={{ fontSize: 38 }} /> },
        { valor: 'Sybase' as TipoTarefa, label: 'Sybase', descricao: 'Backup Sybase Anywhere', icone: <StorageIcon sx={{ fontSize: 38 }} /> },
        { valor: 'Arquivos' as TipoTarefa, label: 'Arquivos', descricao: 'Backup de arquivos e pastas', icone: <InsertDriveFileIcon sx={{ fontSize: 38 }} /> },
        { valor: 'Bat' as TipoTarefa, label: 'Script BAT', descricao: 'Executar arquivo de lote (.bat)', icone: <TerminalIcon sx={{ fontSize: 38 }} /> },
        { valor: 'CheckDB' as TipoTarefa, label: 'Diagnóstico de BD', descricao: 'Verificação de integridade do banco', icone: <FactCheckIcon sx={{ fontSize: 38 }} /> },
    ];

    // Opções para Linux (Ubuntu)
    const opcoesLinux = [
        { valor: 'Postgres' as TipoTarefa, label: 'PostgreSQL', descricao: 'Backup nativo de bases de dados PostgreSQL', icone: <StorageIcon sx={{ fontSize: 38 }} />, recomendado: true },
        { valor: 'Arquivos' as TipoTarefa, label: 'Arquivos e Pastas', descricao: 'Backup de diretórios e arquivos do Linux', icone: <InsertDriveFileIcon sx={{ fontSize: 38 }} /> },
        { valor: 'Bat' as TipoTarefa, label: 'Script Shell (.sh)', descricao: 'Executar comandos shell / bash no Ubuntu', icone: <TerminalIcon sx={{ fontSize: 38 }} /> },
    ];

    const opcoesAtivas = plataformaAtual === 'linux' ? opcoesLinux : opcoesWindows;

    return (
        <Box sx={{ mt: 1, mb: 4 }}>
            {/* 1. SELEÇÃO DA PLATAFORMA */}
            <Typography variant="body1" sx={{ mb: 1.5, fontWeight: 700, color: theme.palette.text.primary }}>
                1. Plataforma do Servidor onde o Agente irá rodar:
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                    <CardPlataforma
                        selecionado={plataformaAtual === 'windows'}
                        onClick={() => selecionarPlataforma('windows')}
                    >
                        <Box
                            sx={{
                                width: 44,
                                height: 44,
                                borderRadius: '10px',
                                bgcolor: plataformaAtual === 'windows' ? 'primary.main' : 'action.selected',
                                color: plataformaAtual === 'windows' ? 'white' : 'text.secondary',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <DesktopWindowsIcon />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                    Windows
                                </Typography>
                                <Chip label="Padrão" size="small" variant={plataformaAtual === 'windows' ? 'filled' : 'outlined'} color="primary" sx={{ height: 20, fontSize: '0.7rem' }} />
                            </Stack>
                            <Typography variant="caption" color="text.secondary">
                                Servidores e estações de trabalho Windows
                            </Typography>
                        </Box>
                    </CardPlataforma>
                </Grid>

                <Grid item xs={12} sm={6}>
                    <CardPlataforma
                        selecionado={plataformaAtual === 'linux'}
                        onClick={() => selecionarPlataforma('linux')}
                    >
                        <Box
                            sx={{
                                width: 44,
                                height: 44,
                                borderRadius: '10px',
                                bgcolor: plataformaAtual === 'linux' ? 'primary.main' : 'action.selected',
                                color: plataformaAtual === 'linux' ? 'white' : 'text.secondary',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <TerminalIcon />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                    Linux (Ubuntu)
                                </Typography>
                                <Chip label="Ubuntu / Debian" size="small" variant={plataformaAtual === 'linux' ? 'filled' : 'outlined'} color="secondary" sx={{ height: 20, fontSize: '0.7rem' }} />
                            </Stack>
                            <Typography variant="caption" color="text.secondary">
                                Servidores Linux Ubuntu com PostgreSQL / Arquivos
                            </Typography>
                        </Box>
                    </CardPlataforma>
                </Grid>
            </Grid>

            {plataformaAtual === 'linux' && (
                <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                    🐧 <strong>Ambiente Linux Selecionado:</strong> O agente Backup Fácil será executado como serviço do <code>systemd</code>. Ao salvar a tarefa, você receberá os 2 comandos rápidos para download e instalação automática no terminal do Ubuntu.
                </Alert>
            )}

            {/* 2. SELEÇÃO DO TIPO DE ORIGEM */}
            <Typography variant="body1" sx={{ mb: 2, fontWeight: 700, color: theme.palette.text.primary }}>
                2. Selecione o tipo de origem para o backup:
            </Typography>

            <Grid container spacing={2}>
                {opcoesAtivas.map((opcao: any) => (
                    <Grid item xs={12} sm={6} md={plataformaAtual === 'linux' ? 4 : 4} key={opcao.valor}>
                        <CardOpcao
                            selecionado={tarefa.tipo === opcao.valor}
                            onClick={() => selecionarTipo(opcao.valor)}
                        >
                            {opcao.recomendado && (
                                <Chip
                                    label="Recomendado"
                                    color="primary"
                                    size="small"
                                    sx={{ position: 'absolute', top: 8, right: 8, height: 18, fontSize: '0.65rem', fontWeight: 700 }}
                                />
                            )}
                            <Box sx={{ color: tarefa.tipo === opcao.valor ? theme.palette.primary.main : theme.palette.text.secondary, mb: 1 }}>
                                {opcao.icone}
                            </Box>
                            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, fontSize: '1rem' }}>
                                {opcao.label}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                                {opcao.descricao}
                            </Typography>
                        </CardOpcao>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
};

export default FormTipoTarefa;
